<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedCompany: {
    type: Object,
    default: null,
  },
  placeholder: {
    type: String,
    default: '',
  },
  /* eslint-disable-next-line vue/no-unused-properties */
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'create', 'remove']);

const { t } = useI18n();
const store = useStore();
const companies = useMapGetter('companies/getCompanies');
const showAlert = useAlert;

const selectedValue = ref(props.selectedCompany?.id || '');
const open = ref(false);
const search = ref('');
const dropdownRef = ref(null);

const companyOptions = computed(() => {
  const list = companies.value || [];
  const searchTerm = search.value.toLowerCase();
  const filtered = search.value
    ? list.filter(company => company.name.toLowerCase().includes(searchTerm))
    : list;

  return filtered.map(company => ({
    value: company.id,
    label: company.name,
    company,
  }));
});

const selectedLabel = computed(() => {
  return (
    props.selectedCompany?.name ||
    props.placeholder ||
    t('CONTACT_FORM.FORM.COMPANY.SELECT_PLACEHOLDER')
  );
});

const canCreateNew = computed(
  () =>
    !!search.value &&
    !(companies.value || []).some(
      c => c.name.toLowerCase() === search.value.toLowerCase()
    )
);

const createOptionLabel = computed(() =>
  t('CONTACT_FORM.FORM.COMPANY.CREATE_LABEL_WITH_NAME', { name: search.value })
);

const loadCompanies = async () => {
  try {
    await store.dispatch('companies/get');
  } catch (error) {
    // Silently handle error
  }
};

const createCompany = async () => {
  if (!search.value.trim()) return;

  try {
    const newCompany = await store.dispatch('companies/create', {
      company: { name: search.value.trim() },
    });

    showAlert(
      t('CONTACT_FORM.FORM.COMPANY.CREATE_SUCCESS', { name: newCompany.name })
    );

    await store.dispatch('companies/get');

    selectedValue.value = newCompany.id;
    emit('create', newCompany);
    emit('select', newCompany);

    search.value = '';
  } catch (error) {
    showAlert(t('CONTACT_FORM.FORM.COMPANY.CREATE_ERROR'));
  }
};

const toggleDropdown = () => {
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    nextTick(() => dropdownRef.value?.focus());
    // Load companies in background without waiting (non-blocking)
    loadCompanies();
  }
};

const selectOption = option => {
  if (option.isCreateOption) {
    createCompany();
    return;
  }

  const company = option.company;

  // Toggle selection: if same company is selected, deselect it
  if (selectedValue.value === option.value) {
    selectedValue.value = '';
    emit('select', null);
  } else {
    selectedValue.value = option.value;
    emit('select', company);
  }
  // Don't close dropdown
};

const deleteCompany = async (company, event) => {
  event.stopPropagation();

  try {
    await store.dispatch('companies/delete', company.id);
    showAlert(
      t('CONTACT_FORM.FORM.COMPANY.DELETE_SUCCESS', { name: company.name })
    );
    if (selectedValue.value === company.id) {
      selectedValue.value = '';
      emit('select', null);
    }
    emit('remove', company);
  } catch (error) {
    showAlert(t('CONTACT_FORM.FORM.COMPANY.DELETE_ERROR'));
  }
};

const isSelected = option => option.value === selectedValue.value;

const onInputSearch = event => {
  search.value = event.target.value;
};

// Watch for changes in selectedCompany prop
watch(
  () => props.selectedCompany,
  newCompany => {
    selectedValue.value = newCompany?.id || '';
  },
  { immediate: true }
);

// Load companies on mount
loadCompanies();
</script>

<template>
  <div
    class="relative min-w-0 group/combobox"
    :class="compact ? 'w-auto max-w-[12.5rem]' : 'w-full'"
  >
    <OnClickOutside @trigger="open = false">
      <!-- Trigger Button - Exact same as ComboBox -->
      <Button
        type="button"
        variant="outline"
        :color="open ? 'blue' : 'slate'"
        :size="compact ? 'xs' : null"
        :label="selectedLabel"
        trailing-icon
        class="justify-between w-full text-n-slate-12 font-normal group-hover/combobox:border-n-slate-6 [&:not(.focused)]:hover:enabled:outline-n-slate-6 [&:not(.focused)]:dark:hover:enabled:outline-n-slate-6 [&:not(.focused)]:dark:outline-n-weak focus:outline-n-brand"
        :class="{
          focused: open,
          '!px-3 !py-2.5': !compact,
        }"
        :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        @click.prevent="toggleDropdown"
      />

      <!-- Dropdown - Exact same structure as ComboBoxDropdown -->
      <div
        v-show="open"
        class="absolute z-50 mt-1 transition-opacity duration-200 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
        :class="compact ? 'min-w-[16rem] w-max max-w-[18rem]' : 'w-full'"
      >
        <!-- Search Input - Exact same as ComboBoxDropdown -->
        <div class="relative border-b border-n-strong">
          <span
            class="absolute i-lucide-search top-2.5 size-4 left-3 text-n-slate-9"
          />
          <input
            ref="dropdownRef"
            :value="search"
            type="search"
            :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
            class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
            @input="onInputSearch"
          />
        </div>

        <!-- Options List -->
        <ul
          class="py-1 mb-0 overflow-auto max-h-[min(50vh,24rem)]"
          role="listbox"
        >
          <!-- Company Options (no loading state to avoid visual gap) -->
          <li
            v-for="option in companyOptions"
            :key="option.value"
            class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-2': isSelected(option),
            }"
            role="option"
            :aria-selected="isSelected(option)"
            @click.prevent="selectOption(option)"
          >
            <span
              :class="{
                'font-medium': isSelected(option),
              }"
              class="text-n-slate-12 flex-1 truncate"
            >
              {{ option.label }}
            </span>

            <div class="flex items-center gap-2">
              <!-- Check Icon -->
              <span
                v-if="isSelected(option)"
                class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
              />

              <!-- Delete Button (for all: real companies call API; virtual only clear selection) -->
              <button
                type="button"
                class="px-2 py-1 text-xs font-medium text-n-slate-11 bg-n-slate-3 rounded hover:bg-n-slate-4 hover:text-n-slate-12 transition-colors duration-150"
                @click.prevent="deleteCompany(option.company, $event)"
              >
                {{ $t('CONTACT_FORM.FORM.COMPANY.DELETE_BUTTON') }}
              </button>
            </div>
          </li>

          <!-- Create New Option -->
          <li
            v-if="canCreateNew"
            class="border-t border-n-strong px-3 py-2 text-sm cursor-pointer hover:bg-n-alpha-2 text-n-brand flex items-center"
            @click.prevent="createCompany"
          >
            <i class="i-lucide-plus w-4 h-4 mr-2" />
            <span>{{ createOptionLabel }}</span>
          </li>

          <!-- Empty State -->
          <li
            v-if="companyOptions.length === 0 && !canCreateNew"
            class="px-3 py-2 text-sm text-n-slate-11"
          >
            {{ t('COMBOBOX.EMPTY_STATE') }}
          </li>
        </ul>
      </div>
    </OnClickOutside>
  </div>
</template>
